import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, X, Star, Trash2, ArrowLeft, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/format";
import SafeImage from "../components/SafeImage";

const BODY_TYPES = ["SUV","Sedan","Hatchback","MPV","Luxury","Performance","Coupe","Convertible"];
const FUELS = ["Petrol","Diesel","Hybrid","Electric","CNG"];
const TRANS = ["Automatic","Manual","AMT","CVT","DCT"];
const OWNS = ["1st","2nd","3rd","4th+"];
const STATUS = ["available","reserved","sold","coming_soon","needs_review","archived"];

const empty = {
  brand: "", model: "", variant: "",
  manufacturing_year: new Date().getFullYear(), registration_year: null, registration_state: "",
  price: 0, kilometres: 0, fuel: "Petrol", transmission: "Automatic",
  body_type: "SUV", ownership: "1st",
  exterior_colour: "", interior_colour: "", engine: "", power: "", torque: "",
  drivetrain: "", insurance_validity: "", seating_capacity: 5, keys_available: "",
  description: "", features: [], custom_specs: [], images: [],
  status: "available", is_featured: false, segment: "standard_premium",
};

const SEGMENTS = [
  { value: "standard_premium", label: "Standard Premium" },
  { value: "luxury", label: "Luxury" },
];

const AdminVehicleEdit = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [featureInput, setFeatureInput] = useState("");

  useEffect(() => {
    if (!isNew) {
      api.get(`/vehicles/${id}`).then((r) => { setF({ ...empty, ...r.data }); setLoading(false); })
        .catch(() => { toast.error("Vehicle not found"); nav("/admin/inventory"); });
    }
  }, [id, isNew, nav]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((file) => fd.append("files", file));
    try {
      const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newImages = r.data.files.map((it, i) => ({
        id: it.id, url: it.url, storage_path: it.storage_path,
        is_cover: (f.images.length === 0 && i === 0),
      }));
      setF({ ...f, images: [...f.images, ...newImages] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const setCover = (idx) => setF({ ...f, images: f.images.map((im, i) => ({ ...im, is_cover: i === idx })) });
  const removeImage = (idx) => setF({ ...f, images: f.images.filter((_, i) => i !== idx) });
  const move = (idx, dir) => {
    const arr = [...f.images];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setF({ ...f, images: arr });
  };

  const addFeature = () => {
    if (!featureInput.trim()) return;
    setF({ ...f, features: [...f.features, featureInput.trim()] });
    setFeatureInput("");
  };

  const save = async () => {
    if (!f.brand || !f.model || !f.manufacturing_year || !f.price) { toast.error("Brand, model, year and price are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        manufacturing_year: Number(f.manufacturing_year),
        registration_year: f.registration_year ? Number(f.registration_year) : null,
        price: Number(f.price),
        kilometres: Number(f.kilometres) || 0,
        seating_capacity: Number(f.seating_capacity) || 5,
      };
      if (isNew) {
        const r = await api.post("/admin/vehicles", payload);
        toast.success("Vehicle created");
        nav(`/admin/inventory/${r.data.id}`);
      } else {
        await api.put(`/admin/vehicles/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-vehicle-edit">
      <button onClick={() => nav("/admin/inventory")} className="text-sm text-[#242424]/70 inline-flex items-center gap-2"><ArrowLeft size={14} /> Back</button>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0 flex-1">
          <div className="overline text-[#7A1519]">{isNew ? "New Vehicle" : "Edit Vehicle"}</div>
          <h1 className="font-display text-2xl sm:text-3xl tracking-tight font-semibold break-words">{isNew ? "Add a new vehicle" : `${f.manufacturing_year} ${f.brand} ${f.model}`}</h1>
          {!isNew && f.external_source && (
            <div className="mt-2 inline-flex items-center gap-2 border hairline bg-white px-3 py-1.5 rounded-sm text-xs flex-wrap max-w-full" data-testid="ve-source-badge">
              <span className="uppercase tracking-[0.18em] font-semibold text-[#7A1519]">Source: {f.external_source}</span>
              {f.external_id && <span className="text-[#242424]/70">· ID <span className="font-mono break-all">{f.external_id}</span></span>}
              {f.external_last_synced && <span className="text-[#242424]/60">· synced {new Date(f.external_last_synced).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          {!isNew && f.slug && (
            <a
              href={`/cars/${f.slug}`}
              target="_blank"
              rel="noreferrer"
              className="border hairline bg-white px-4 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2 hover:border-[#7A1519] hover:text-[#7A1519] transition-colors"
              data-testid="ve-view-live"
              title="Open this vehicle's public listing in a new tab"
            >
              <ExternalLink size={14} /> View Live
            </a>
          )}
          <button onClick={save} disabled={busy} className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-60" data-testid="ve-save">{busy ? "Saving…" : "Save Vehicle"}</button>
        </div>
      </div>

      <div className="bg-white border hairline p-4 sm:p-6 rounded-sm space-y-4">
        <SectionTitle>Basics</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Input label="Brand *" value={f.brand} onChange={(v) => setF({ ...f, brand: v })} testId="ve-brand" />
          <Input label="Model *" value={f.model} onChange={(v) => setF({ ...f, model: v })} testId="ve-model" />
          <Input label="Variant" value={f.variant} onChange={(v) => setF({ ...f, variant: v })} testId="ve-variant" />
          <Input label="Manufacturing Year *" type="number" value={f.manufacturing_year} onChange={(v) => setF({ ...f, manufacturing_year: v })} testId="ve-mfg" />
          <Input label="Registration Year" type="number" value={f.registration_year || ""} onChange={(v) => setF({ ...f, registration_year: v })} testId="ve-reg" />
          <Input label="Registration State" value={f.registration_state} onChange={(v) => setF({ ...f, registration_state: v })} testId="ve-reg-state" />
          <Input label="Price (₹) *" type="number" value={f.price} onChange={(v) => setF({ ...f, price: v })} testId="ve-price" />
          <Input label="Kilometres *" type="number" value={f.kilometres} onChange={(v) => setF({ ...f, kilometres: v })} testId="ve-km" />
          <Select label="Body Type" value={f.body_type} onChange={(v) => setF({ ...f, body_type: v })} options={BODY_TYPES} testId="ve-body" />
          <Select label="Fuel" value={f.fuel} onChange={(v) => setF({ ...f, fuel: v })} options={FUELS} testId="ve-fuel" />
          <Select label="Transmission" value={f.transmission} onChange={(v) => setF({ ...f, transmission: v })} options={TRANS} testId="ve-trans" />
          <Select label="Ownership" value={f.ownership} onChange={(v) => setF({ ...f, ownership: v })} options={OWNS} testId="ve-own" />
          <Select label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUS} testId="ve-status" />
          <div>
            <label className="overline text-[#242424]/60 block mb-2">Inventory Segment</label>
            <select
              value={f.segment || "standard_premium"}
              onChange={(e) => setF({ ...f, segment: e.target.value })}
              className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-2.5 text-sm"
              data-testid="ve-segment"
            >
              {SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 mt-6">
            <button type="button" onClick={() => setF({ ...f, is_featured: !f.is_featured })} className={`inline-flex items-center gap-2 px-3 py-2 border hairline rounded-sm text-sm ${f.is_featured ? "bg-[#7A1519] text-white border-[#7A1519]" : ""}`} data-testid="ve-feature-toggle">
              <Star size={14} fill={f.is_featured ? "currentColor" : "none"} /> Featured
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border hairline p-4 sm:p-6 rounded-sm space-y-4">
        <SectionTitle>Technical</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Input label="Engine" value={f.engine} onChange={(v) => setF({ ...f, engine: v })} testId="ve-engine" />
          <Input label="Power" value={f.power} onChange={(v) => setF({ ...f, power: v })} testId="ve-power" />
          <Input label="Torque" value={f.torque} onChange={(v) => setF({ ...f, torque: v })} testId="ve-torque" />
          <Input label="Drivetrain" value={f.drivetrain} onChange={(v) => setF({ ...f, drivetrain: v })} testId="ve-drive" />
          <Input label="Exterior Colour" value={f.exterior_colour} onChange={(v) => setF({ ...f, exterior_colour: v })} testId="ve-ext-col" />
          <Input label="Interior Colour" value={f.interior_colour} onChange={(v) => setF({ ...f, interior_colour: v })} testId="ve-int-col" />
          <Input label="Insurance Validity" value={f.insurance_validity} onChange={(v) => setF({ ...f, insurance_validity: v })} testId="ve-ins" />
          <Input label="Seating Capacity" type="number" value={f.seating_capacity} onChange={(v) => setF({ ...f, seating_capacity: v })} testId="ve-seats" />
          <Input label="Keys Available" value={f.keys_available} onChange={(v) => setF({ ...f, keys_available: v })} testId="ve-keys" />
        </div>
      </div>

      <div className="bg-white border hairline p-4 sm:p-6 rounded-sm space-y-4">
        <SectionTitle>Description</SectionTitle>
        <textarea rows={6} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-3 text-sm" data-testid="ve-desc" />
      </div>

      <div className="bg-white border hairline p-4 sm:p-6 rounded-sm space-y-4">
        <SectionTitle>Features</SectionTitle>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder="e.g., Panoramic Roof" className="flex-1 bg-[#F7F6F3] border hairline px-4 py-2.5 rounded-sm text-sm" data-testid="ve-feature-input" />
          <button type="button" onClick={addFeature} className="bg-[#111111] text-white px-4 py-2.5 rounded-sm text-sm">Add</button>
        </div>
        <div className="flex flex-wrap gap-2">
          {f.features.map((ft, i) => (
            <span key={i} className="inline-flex items-center gap-2 border hairline bg-[#F7F6F3] px-3 py-1.5 rounded-sm text-xs">
              {ft}
              <button type="button" onClick={() => setF({ ...f, features: f.features.filter((_, x) => x !== i) })}><X size={10} /></button>
            </span>
          ))}
        </div>
      </div>

      <div className="bg-white border hairline p-4 sm:p-6 rounded-sm space-y-4">
        <SectionTitle>Images ({f.images.length})</SectionTitle>
        <label className="border-2 border-dashed hairline block p-8 text-center cursor-pointer hover:border-[#7A1519] rounded-sm">
          <UploadCloud className="mx-auto text-[#7A1519]" />
          <div className="mt-2 text-sm">{uploading ? "Uploading…" : "Upload one or more images"}</div>
          <input type="file" accept="image/*" multiple hidden disabled={uploading} onChange={(e) => upload(e.target.files)} data-testid="ve-upload-input" />
        </label>
        {f.images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {f.images.map((im, i) => (
              <div key={im.id || i} className={`relative border-2 rounded-sm overflow-hidden ${im.is_cover ? "border-[#7A1519]" : "border-transparent"}`}>
                <div className="aspect-[4/3] bg-[#F7F6F3]"><SafeImage src={resolveImageUrl(im.url)} alt="" className="w-full h-full object-cover" /></div>
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] flex items-center justify-between px-2 py-1">
                  <button onClick={() => setCover(i)} className="hover:underline">{im.is_cover ? "Cover" : "Set cover"}</button>
                  <div className="flex gap-1">
                    <button onClick={() => move(i, -1)}>◀</button>
                    <button onClick={() => move(i, 1)}>▶</button>
                    <button onClick={() => removeImage(i)}><Trash2 size={10} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom save row — ensures the Save/Create button is always reachable
          after scrolling on mobile without jumping back to the top header. */}
      <div className="sticky bottom-0 -mx-4 sm:mx-0 sm:static bg-[#F7F6F3]/95 sm:bg-transparent backdrop-blur sm:backdrop-blur-none border-t hairline sm:border-0 px-4 sm:px-0 py-3 sm:py-0 z-20">
        <button
          onClick={save}
          disabled={busy}
          className="w-full sm:w-auto bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-60"
          data-testid="ve-save-bottom"
        >
          {busy ? "Saving…" : (isNew ? "Create Vehicle" : "Save Vehicle")}
        </button>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => <div className="overline text-[#7A1519]">{children}</div>;

const Input = ({ label, value, onChange, type = "text", testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-2.5 text-sm" data-testid={testId} />
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="overline text-[#242424]/60 block mb-2">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#F7F6F3] border hairline rounded-sm px-4 py-2.5 text-sm" data-testid={testId}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default AdminVehicleEdit;
